/**
 * CV View & Job View Tracking API Service
 * - CV views: tracking of companies that have viewed candidate's CV
 * - Job views: tracking of candidate job page views with platform info
 */

import http from '../client';
import { getTrackedSource } from '../../utils/sourceDetection';
import { isTokiWebView } from '../../utils/tokiBridge';
import { getViewSessionId, localGet } from '../../utils/trackingSession';

// Standardized response handlers
const handleApiSuccess = (response) => ({
  success: true,
  data: response.data?.data || response.data,
  status: response.status,
  message: response.data?.message || 'Success'
});

const handleApiError = (error) => ({
  success: false,
  error: error.message || 'An error occurred',
  status: error.status || 500,
  requiresAuth: error.status === 401
});

/**
 * Get count of companies that have viewed current user's CV
 * @returns {Promise<Object>} { success, data: { count } }
 */
export const getViewedCount = async () => {
  try {
    const response = await http.get('/cv-view/count');
    return handleApiSuccess(response);
  } catch (error) {
    return handleApiError(error);
  }
};

/**
 * Get list of companies that have viewed current user's CV
 * @returns {Promise<Object>} { success, data: [{ companyId, companyName, companyLogo, viewedAt }] }
 */
export const getViewedList = async () => {
  try {
    const response = await http.get('/cv-view/list');
    return handleApiSuccess(response);
  } catch (error) {
    return handleApiError(error);
  }
};

// --- Job View Tracking ---

// Lives in utils/trackingSession (storage-safe, shared with the funnel events); re-exported
// here so existing imports keep working.
export { getViewSessionId };

/**
 * Detect the current platform: TOKI, HAMBI, or DIIL. Never throws — it runs inside
 * click handlers via tracking.js, and storage can be blocked.
 */
export const detectPlatform = () => {
  try {
    if (isTokiWebView()) return 'TOKI';
  } catch (error) {
    // fall through to the stored-session checks
  }
  if (localGet('toki_session')) return 'TOKI';
  if (localGet('hambi_session')) return 'HAMBI';
  return 'DIIL';
};

/**
 * Record a job view (works for both authenticated and anonymous users)
 * @param {number} assessmentId - Assessment ID being viewed
 * @returns {Promise<Object>}
 */
export const recordJobView = async (assessmentId) => {
  try {
    const tracked = getTrackedSource();
    const params = {
      assessmentId,
      platform: detectPlatform(),
      sessionId: getViewSessionId(),
    };
    if (tracked?.source) {
      params.sourceChannel = tracked.source;
    }
    const response = await http.post('/job-view/record', null, { params });
    return handleApiSuccess(response);
  } catch (error) {
    // View tracking should never break the user experience
    console.warn('Failed to record job view:', error);
    return handleApiError(error);
  }
};

/**
 * Record a home page view (assessmentId=0 means general platform visit)
 * Deduplicates by sessionId so each session only records once.
 * @returns {Promise<Object>}
 */
export const recordHomeView = async () => {
  try {
    const tracked = getTrackedSource();
    const params = {
      assessmentId: 0,
      platform: detectPlatform(),
      sessionId: getViewSessionId(),
    };
    if (tracked?.source) {
      params.sourceChannel = tracked.source;
    }
    const response = await http.post('/job-view/record', null, { params });
    return handleApiSuccess(response);
  } catch (error) {
    console.warn('Failed to record home view:', error);
    return handleApiError(error);
  }
};

const jobViewService = {
  getViewedCount,
  getViewedList,
  recordJobView,
  recordHomeView,
  detectPlatform
};

export default jobViewService;
